// Plan 24c Phase B, Task 8 — Pause Menu screen (Screen 31)
//
// Full-viewport system menu overlay.
// Menu-only mode: shows CONTINUE + SETTINGS + HOW TO PLAY + LEAVE GAME, ESC closes.
// Paused host mode: shows RESUME + SETTINGS + HOW TO PLAY + LEAVE GAME, ESC resumes.
// Paused readonly mode: shows SETTINGS + HOW TO PLAY + LEAVE GAME, ESC is a no-op.
//
// Design tokens (docs/design/tokens.json):
//   typography.family.headline = "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif"
//   typography.family.body     = "'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif"
//   typography.shadow.title    = "0 3px 0 #000000, 0 6px 12px rgba(0,0,0,0.50)"
//   color.text.secondary       = "#B0B8C8"
//   color.ui.overlay-dim       = "rgba(0, 0, 0, 0.60)"
//   color.ui.panel-bg-alpha    = "rgba(30, 35, 50, 0.82)"
//   spacing.lg                 = "24px"
//   spacing.md                 = "16px"
//   z-index.loading            = 600

import { createButton } from '@client/ui/components/button';
import type { UIComponentHandle } from '@client/ui/components/types';
import type { SettingsPayload } from './settings';
import type { ScreenHandle, ScreenMountContext } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type PauseMenuRoomType = 'create-game' | 'quick-play';
export type PauseMenuState = 'menu-only' | 'server-paused-host' | 'server-paused-readonly';

export type PauseMenuPayload = {
  /** Whether the local player is the host. Kept for compatibility with older callers. */
  isHost: boolean;
  roomType: PauseMenuRoomType;
  /** Default preserves legacy paused host/non-host behavior from isHost. */
  menuState?: PauseMenuState;
  /** Default: "PAUSED". */
  title?: string;
  /** Default: state-specific copy. */
  subtitle?: string;
  /** Default: "RESUME". */
  resumeLabel?: string;
  /** Default: "CONTINUE". */
  continueLabel?: string;
  /** Default: "LEAVE GAME". */
  leaveLabel?: string;
  /** Default: dispose the pause menu. Menu-only only. */
  onContinue?: () => void;
  /** Default: dispose the pause menu. Paused-host only. */
  onResume?: () => void;
  /** Default: navigate to 'settings'. */
  onSettings?: () => void;
  /** Default: navigate to the shared How To Play screen. */
  onHowToPlay?: () => void;
  /** Optional warmup/lobby action shown above Leave. */
  onReadyToggle?: () => void;
  /** Label for the optional ready toggle action. */
  readyToggleLabel?: string;
  /** Default: navigate to 'confirm-dialog' with variant 'leave-game'. */
  onLeaveGame?: () => void;
};

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createPauseMenuScreen(ctx: ScreenMountContext<PauseMenuPayload>): ScreenHandle {
  const { host, gsapRegistry, router, payload } = ctx;
  const { isHost, onResume, onSettings, onLeaveGame } = payload;
  const menuState: PauseMenuState =
    payload.menuState ?? (isHost ? 'server-paused-host' : 'server-paused-readonly');
  const canContinue = menuState === 'menu-only';
  const canResume = menuState === 'server-paused-host';
  const isReadonlyPaused = menuState === 'server-paused-readonly';

  let disposed = false;

  // Collected button handles for disposal
  const buttonHandles: UIComponentHandle[] = [];

  // ── Default action callbacks ────────────────────────────────────────────────

  function doContinue(): void {
    if (payload.onContinue !== undefined && payload.onContinue !== null) {
      payload.onContinue();
    } else {
      handle.dispose();
    }
  }

  function doResume(): void {
    if (onResume !== undefined && onResume !== null) {
      onResume();
    } else {
      handle.dispose();
    }
  }

  function doSettings(): void {
    if (onSettings !== undefined && onSettings !== null) {
      onSettings();
    } else {
      const settingsPayload: SettingsPayload = {
        backTarget: {
          screenId: 'pause-menu',
          payload,
        },
      };
      router.navigate('settings', settingsPayload);
    }
  }

  function doHowToPlay(): void {
    if (payload.onHowToPlay !== undefined && payload.onHowToPlay !== null) {
      payload.onHowToPlay();
      return;
    }
    router.navigate('how-to-play', {
      onBack: () => router.navigate('pause-menu', payload),
    });
  }

  function doReadyToggle(): void {
    payload.onReadyToggle?.();
  }

  function doLeaveGame(): void {
    if (onLeaveGame !== undefined && onLeaveGame !== null) {
      onLeaveGame();
    } else {
      router.navigate('confirm-dialog', { variant: 'leave-game' });
    }
  }

  // ── Build root overlay ──────────────────────────────────────────────────────

  const root = document.createElement('div');
  root.setAttribute('data-screen', 'pause-menu');
  Object.assign(root.style, {
    position: 'fixed',
    inset: '0',
    zIndex: '920',
    background: 'rgba(0,0,0,0.60)', // color.ui.overlay-dim
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    padding:
      'max(12px, var(--app-safe-area-top)) max(12px, var(--app-safe-area-right)) max(12px, var(--app-safe-area-bottom)) max(12px, var(--app-safe-area-left))',
  });

  // ── Build centered panel ────────────────────────────────────────────────────

  const panel = document.createElement('div');
  Object.assign(panel.style, {
    width: 'min(400px, calc(var(--app-viewport-width, 100vw) - 72px))',
    maxWidth: 'calc(var(--app-viewport-width, 100vw) - 24px)',
    maxHeight: 'calc(var(--app-viewport-height, 100vh) - 24px)',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    boxSizing: 'border-box',
    padding: '24px 28px', // layout.panel-padding-lg
    borderRadius: '12px', // border-radius.lg
    background: 'rgba(30,35,50,0.82)', // color.ui.panel-bg-alpha
    boxShadow: '0 8px 32px rgba(0,0,0,0.50)', // shadow.modal
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px', // spacing.md
  });

  // ── Logo ────────────────────────────────────────────────────────────────────

  const logoEl = document.createElement('h5');
  logoEl.textContent = 'SMASHPARTY';
  Object.assign(logoEl.style, {
    margin: '0',
    fontFamily: "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif", // typography.family.headline
    fontSize: '18px', // typography.size.h5
    color: '#FFFFFF',
    textAlign: 'center',
  });
  panel.appendChild(logoEl);

  // ── Title ───────────────────────────────────────────────────────────────────

  const titleEl = document.createElement('h1');
  titleEl.textContent = payload.title ?? (canContinue ? 'MENU' : 'PAUSED');
  Object.assign(titleEl.style, {
    margin: '0',
    fontFamily: "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif", // typography.family.headline
    fontSize: '48px', // typography.size.h1
    color: '#FFFFFF',
    textShadow: '0 3px 0 #000000, 0 6px 12px rgba(0,0,0,0.50)', // typography.shadow.title
    textAlign: 'center',
  });
  panel.appendChild(titleEl);

  // ── Subtitle ─────────────────────────────────────────────────────────────────

  const subtitleEl = document.createElement('p');
  Object.assign(subtitleEl.style, {
    margin: '0',
    fontFamily: "'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif", // typography.family.body
    fontSize: '16px', // typography.size.body
    color: '#B0B8C8', // color.text.secondary
    textAlign: 'center',
  });

  if (payload.subtitle != null) {
    subtitleEl.textContent = payload.subtitle;
  } else if (isReadonlyPaused) {
    // Non-host: pulsing dots animation
    subtitleEl.textContent = 'Host paused the game. ';
    const dotsSpan = document.createElement('span');
    dotsSpan.setAttribute('data-pulsing-dots', '');
    dotsSpan.textContent = '...';
    subtitleEl.appendChild(dotsSpan);

    // Start pulsing animation via gsapRegistry
    const dotsHandle = gsapRegistry.create('pause-menu:dots-pulse');
    dotsHandle.timeline.to(dotsSpan, { opacity: 0.4, duration: 1, repeat: -1, yoyo: true });
    buttonHandles.push(dotsHandle as unknown as UIComponentHandle);
  } else if (canResume) {
    subtitleEl.textContent = 'Host paused the game.';
  } else {
    subtitleEl.textContent = 'Game options';
  }
  panel.appendChild(subtitleEl);

  // ── Button stack ─────────────────────────────────────────────────────────────

  const buttonStack = document.createElement('div');
  Object.assign(buttonStack.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px', // spacing.md
    width: '100%',
  });

  // CONTINUE — menu-only contexts
  if (canContinue) {
    const continueHandle = createButton({
      gsapRegistry,
      mount: undefined,
      variant: 'primary',
      size: 'large',
      label: payload.continueLabel ?? 'CONTINUE',
      onClick: () => {
        if (!disposed) doContinue();
      },
    });
    buttonHandles.push(continueHandle);
    buttonStack.appendChild(continueHandle.element);
  }

  // RESUME — paused host contexts
  if (canResume) {
    const resumeHandle = createButton({
      gsapRegistry,
      mount: undefined,
      variant: 'primary',
      size: 'large',
      label: payload.resumeLabel ?? 'RESUME',
      onClick: () => {
        if (!disposed) doResume();
      },
    });
    buttonHandles.push(resumeHandle);
    buttonStack.appendChild(resumeHandle.element);
  }

  // SETTINGS — always
  const settingsHandle = createButton({
    gsapRegistry,
    mount: undefined,
    variant: 'secondary',
    size: 'large',
    label: 'SETTINGS',
    onClick: () => {
      if (!disposed) doSettings();
    },
  });
  buttonHandles.push(settingsHandle);
  buttonStack.appendChild(settingsHandle.element);

  // HOW TO PLAY — shared screen used by pause and warmup menus
  const howToPlayHandle = createButton({
    gsapRegistry,
    mount: undefined,
    variant: 'secondary',
    size: 'large',
    label: 'HOW TO PLAY',
    onClick: () => {
      if (!disposed) doHowToPlay();
    },
  });
  buttonHandles.push(howToPlayHandle);
  buttonStack.appendChild(howToPlayHandle.element);

  if (payload.onReadyToggle != null) {
    const readyToggleHandle = createButton({
      gsapRegistry,
      mount: undefined,
      variant: 'secondary',
      size: 'large',
      label: payload.readyToggleLabel ?? 'READY',
      onClick: () => {
        if (!disposed) doReadyToggle();
      },
    });
    buttonHandles.push(readyToggleHandle);
    buttonStack.appendChild(readyToggleHandle.element);
  }

  // LEAVE GAME — always
  const leaveHandle = createButton({
    gsapRegistry,
    mount: undefined,
    variant: 'danger',
    size: 'large',
    label: payload.leaveLabel ?? 'LEAVE GAME',
    onClick: () => {
      if (!disposed) doLeaveGame();
    },
  });
  buttonHandles.push(leaveHandle);
  buttonStack.appendChild(leaveHandle.element);

  panel.appendChild(buttonStack);

  // ── Mount root ──────────────────────────────────────────────────────────────

  root.appendChild(panel);
  host.appendChild(root);

  function handleOutsidePointerDown(event: PointerEvent): void {
    if (disposed || !canContinue) return;
    if (router.overlayCurrent != null && router.overlayCurrent !== 'pause-menu') return;
    const target = event.target;
    if (target instanceof Node && panel.contains(target)) return;
    doContinue();
  }

  function stopPanelClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  document.addEventListener('pointerdown', handleOutsidePointerDown, true);
  panel.addEventListener('click', stopPanelClick);

  // ── ESC handler ─────────────────────────────────────────────────────────────
  // ESC: menu-only closes, paused host resumes, paused readonly is a no-op.

  function handleEsc(e: KeyboardEvent): void {
    if (e.key !== 'Escape') return;
    if (disposed) return;
    if (router.overlayCurrent != null && router.overlayCurrent !== 'pause-menu') return;
    if (canContinue) {
      doContinue();
      return;
    }
    if (canResume) doResume();
  }

  document.addEventListener('keydown', handleEsc);

  // ── dispose ──────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Remove ESC listener
    document.removeEventListener('keydown', handleEsc);
    document.removeEventListener('pointerdown', handleOutsidePointerDown, true);
    panel.removeEventListener('click', stopPanelClick);

    // Dispose all button handles (and dots-pulse timeline handle for non-host)
    for (const h of buttonHandles) {
      h.dispose();
    }

    // Remove root from DOM
    root.remove();
  }

  // Define handle so doResume can reference it for the no-onResume case
  const handle: ScreenHandle = { element: root, dispose };
  return handle;
}
